import { useState, type FormEvent } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { authErrorMessage, resetPassword } from "@/lib/auth";
import { Button, Card } from "@/components/ui";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

/**
 * Set a new password from a reset link.
 *
 * This route did not exist. The reset email has always pointed at
 * `/reset-password?token=…`, so requesting a reset worked, the email arrived,
 * and following it landed on nothing — password reset was dead end to end, the
 * same way accepting an invitation was.
 *
 * Public by necessity: somebody who cannot sign in is precisely who needs it.
 * The token in the query string is the entire credential, which is why it is
 * never shown, logged, or put in a link the page itself renders.
 */
export function ResetPassword() {
  const { t } = useTranslation("auth");
  const token = new URLSearchParams(window.location.search).get("token");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!token) return;

    setError(null);
    setPending(true);

    const { error: resetError } = await resetPassword({ newPassword: password, token });

    setPending(false);

    if (resetError) {
      setError(authErrorMessage(resetError));
      return;
    }

    setDone(true);
  }

  if (!token) {
    return (
      <Shell>
        <h1 className="text-[16px] font-semibold text-text">{t("reset.incompleteTitle")}</h1>
        <p className="mt-1.5 text-[13.5px] leading-relaxed text-text-muted">
          {t("reset.incompleteBody")}
        </p>
        <Button
          variant="secondary"
          className="mt-4 w-full"
          onClick={() => window.location.assign("/")}
        >
          {t("reset.backToSignIn")}
        </Button>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <h1 className="text-[16px] font-semibold text-text">{t("reset.doneTitle")}</h1>
        <p className="mt-1.5 text-[13.5px] leading-relaxed text-text-muted">
          {t("reset.doneBody")}
        </p>
        <Button
          variant="primary"
          className="mt-4 w-full"
          onClick={() => window.location.assign("/")}
        >
          {t("reset.goToSignIn")}
        </Button>
      </Shell>
    );
  }

  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = password.length >= 12 && confirm === password;

  return (
    <Shell>
      <div className="mb-5">
        <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-accent-subtle text-accent">
          <KeyRound size={17} />
        </div>
        <h1 className="text-[19px] font-semibold tracking-tight text-text">
          {t("reset.title")}
        </h1>
        <p className="mt-1.5 text-[13.5px] leading-relaxed text-text-muted">
          {t("reset.hint")}
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-3.5">
        <label className="block">
          <span className="mb-1.5 block text-[13px] font-medium text-text">
            {t("reset.newPassword")}
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            autoFocus
            className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm text-text focus:border-accent focus:outline-none"
          />
          {tooShort ? (
            <span className="mt-1.5 block text-[12px] text-warning">
              {t("password.moreNeeded", { count: 12 - password.length })}
            </span>
          ) : null}
        </label>

        <label className="block">
          <span className="mb-1.5 block text-[13px] font-medium text-text">
            {t("reset.confirm")}
          </span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm text-text focus:border-accent focus:outline-none"
          />
          {mismatch ? (
            <span className="mt-1.5 block text-[12px] text-warning">
              {t("reset.mismatch")}
            </span>
          ) : null}
        </label>

        {error ? (
          <p
            role="alert"
            className="rounded-lg bg-danger-subtle px-3 py-2 text-[13px] leading-relaxed text-danger"
          >
            {error}
          </p>
        ) : null}

        <Button
          type="submit"
          variant="primary"
          disabled={!canSubmit || pending}
          className="w-full"
        >
          {pending ? <Loader2 size={15} className="animate-spin" /> : null}
          {t("reset.submit")}
        </Button>
      </form>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-bg px-5 py-10">
      <Card className="w-full max-w-[400px] p-7">{children}</Card>
      <div className="mt-5">
        <LanguageSwitcher variant="select" />
      </div>
    </div>
  );
}
